<template>
  <div :class="wrapperClasses">
    <!-- Label -->
    <label v-if="label" :for="id" :class="labelClasses">
      {{ label }}
      <span v-if="required" class="text-danger ml-1">*</span>
    </label>

    <!-- Select wrapper -->
    <div class="relative">
      <select
        :id="id"
        :value="modelValue"
        :disabled="disabled"
        :required="required"
        :class="selectClasses"
        @change="handleChange"
        @blur="handleBlur"
        @focus="handleFocus"
      >
        <!-- Placeholder option -->
        <option v-if="placeholder" value="" disabled selected>{{ placeholder }}</option>

        <!-- Options from array -->
        <option
          v-for="option in normalizedOptions"
          :key="option.value"
          :value="option.value"
          :disabled="option.disabled"
        >
          {{ option.label }}
        </option>
      </select>

      <!-- Dropdown icon -->
      <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
        <svg class="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
        </svg>
      </div>
    </div>

    <!-- Helper text or error message -->
    <p v-if="error" class="mt-1 text-sm text-danger">{{ error }}</p>
    <p v-else-if="hint" class="mt-1 text-sm text-gray-600">{{ hint }}</p>
  </div>
</template>

<script>
export default {
  name: 'BaseSelect',
  props: {
    modelValue: {
      type: [String, Number, Boolean],
      default: ''
    },
    id: {
      type: String,
      default: () => `select-${Math.random().toString(36).substr(2, 9)}`
    },
    label: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: null
    },
    options: {
      type: Array,
      required: true,
      // Options can be:
      // 1. Array of strings: ['Option 1', 'Option 2']
      // 2. Array of objects: [{ label: 'Option 1', value: 'opt1' }]
      validator: (value) => Array.isArray(value)
    },
    hint: {
      type: String,
      default: null
    },
    error: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['sm', 'md', 'lg'].includes(value)
    }
  },
  computed: {
    normalizedOptions() {
      return this.options.map(option => {
        // If option is a string, convert to object
        if (typeof option === 'string') {
          return {
            label: option,
            value: option,
            disabled: false
          }
        }

        // If option is already an object
        return {
          label: option.label || option.value,
          value: option.value,
          disabled: option.disabled || false
        }
      })
    },
    wrapperClasses() {
      return 'w-full'
    },
    labelClasses() {
      return 'block mb-1 text-sm font-medium text-gray-700'
    },
    selectClasses() {
      return [
        'block w-full pr-10 border border-gray-300 rounded-md shadow-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-primary-light appearance-none',
        this.sizeClasses,
        this.error ? 'border-danger focus:ring-red-500 focus:border-red-500' : '',
        this.disabled ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'bg-white cursor-pointer'
      ]
    },
    sizeClasses() {
      const sizes = {
        sm: 'px-3 py-1.5 text-sm',
        md: 'px-4 py-2 text-base',
        lg: 'px-5 py-3 text-lg'
      }
      return sizes[this.size]
    }
  },
  methods: {
    handleChange(event) {
      let value = event.target.value

      // Try to convert to number if original value was a number
      const originalOption = this.normalizedOptions.find(opt => opt.value.toString() === value)
      if (originalOption && typeof originalOption.value === 'number') {
        value = Number(value)
      }

      this.$emit('update:modelValue', value)
      this.$emit('change', value)
    },
    handleBlur(event) {
      this.$emit('blur', event)
    },
    handleFocus(event) {
      this.$emit('focus', event)
    }
  }
}
</script>
